"use client";

import React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
} from "@mui/material";
import {
  Close as CloseIcon,
  Article as ArticleIcon,
} from "@mui/icons-material";
import { useLocationAllowed } from "./LocationProvider";

const SESSION_STORAGE_KEY = "macedonian-journalism-popup-shown-v1";

// Google Analytics gtag types
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Google Analytics tracking helper
const trackEvent = (
  eventName: string,
  parameters: { [key: string]: any } = {}
) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, {
        event_category: "macedonian_Journalism_Popup",
        event_label: "Independent_Journalism_Support",
        ...parameters,
      });
      console.log(`📊 GA Event: ${eventName}`, parameters);
    }
  } catch (error) {
    console.warn("Google Analytics tracking error:", error);
  }
};

interface DonatePopupProps {
  onDonate?: () => void;
  onClose?: () => void;
  gtagId?: string; // Optional GA4 measurement ID
}

export const DonatePopup: React.FC<DonatePopupProps> = ({
  onDonate,
  onClose,
  gtagId,
}) => {
  const [open, setOpen] = useState(false);
  const { isAllowed, loading } = useLocationAllowed();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const wasShown = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!loading && isAllowed && !wasShown) {
      const timer = setTimeout(() => {
        setOpen(true);

        // Track popup display - main event
        trackEvent("popup_shown");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [loading, isAllowed, isMobile]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");

    // Track popup close
    trackEvent("popup_closed");

    onClose?.();
  };

  const handleDonate = () => {
    setOpen(false);
    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");

    // Track donation click - main conversion event
    trackEvent("donate_clicked", {
      event_category: "Conversion",
      event_label: "Macedonian_Journalism_Donation",
      value: 1,
      conversion: true,
    });

    onDonate?.();
  };

  const handleBackdropClick = () => {
    // Track backdrop/outside click as popup closed
    trackEvent("popup_closed", {
      close_method: "backdrop_click",
      device_type: isMobile ? "mobile" : "desktop",
    });
  };

  if (loading || !isAllowed) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleBackdropClick}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 1,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          maxWidth: 480,
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "#666666",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogTitle sx={{ pt: 3, pb: 2, pr: 6 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <ArticleIcon sx={{ fontSize: 24, color: "#d32f2f" }} />
          <Typography
            variant="h6"
            component="h2"
            fontWeight="600"
            color="#1a1a1a"
          >
            Вистината има цена
          </Typography>
        </Box>
      </DialogTitle>

      <Divider sx={{ mx: 3 }} />

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Typography
          variant="body1"
          color="#333333"
          lineHeight={1.6}
          mb={2.5}
          fontWeight="500"
        >
          Во време кога лажните вести се шират како пожар, независното
          новинарство е единствената одбрана на вистината.
        </Typography>

        <Typography variant="body2" color="#555555" lineHeight={1.5} mb={2.5}>
          <strong>
            Без вашата поддршка, критичните истории ќе останат неиспричани.
          </strong>{" "}
          Секоја донација, без разлика колку мала, ни овозможува да продолжиме
          да ги разоткриваме фактите кои моќните сакаат да ги сокријат.
        </Typography>

        <Box
          sx={{
            backgroundColor: "#fff3e0",
            p: 2.5,
            borderRadius: 1,
            border: "1px solid #ffcc02",
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            color="#e65100"
            lineHeight={1.5}
            mb={1}
            fontWeight="600"
          >
            🚨 Итно потребна поддршка:
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2,
              "& li": { mb: 0.5, fontSize: "0.875rem", color: "#bf360c" },
            }}
          >
            <li>Истражувачко новинарство под закана</li>
            <li>Медиумската слобода се намалува</li>
            <li>Корупцијата мора да биде разоткриена</li>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="#2e7d32"
          lineHeight={1.5}
          fontStyle="italic"
        >
          "Кога граѓаните се информирани, демократијата процветува. Вашата
          донација е инвестиција во иднината на нашето општество."
        </Typography>
      </DialogContent>

      <Divider sx={{ mx: 3 }} />

      <DialogActions
        sx={{ p: 3, gap: 1.5, flexDirection: isMobile ? "column" : "row" }}
      >
        <a
          href="https://www.gofundme.com/f/mevv7-donirajte-za-nasiot-medium-da-ostane-nezavisen"
          target="_blank"
        >
          <Button
            onClick={handleDonate}
            variant="contained"
            size="medium"
            fullWidth={isMobile}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              py: 1.2,
              px: 3,
              borderRadius: 1,
              fontWeight: "600",
              fontSize: "0.9rem",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#b71c1c",
                boxShadow: "0 2px 8px rgba(211, 47, 47, 0.4)",
              },
            }}
          >
            Донирај сега - Заштити ја вистината
          </Button>
        </a>

        <Button
          onClick={handleClose}
          variant="text"
          size="medium"
          fullWidth={isMobile}
          sx={{
            color: "#666666",
            py: 1,
            px: 3,
            borderRadius: 1,
            fontWeight: "400",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Можеби подоцна
        </Button>
      </DialogActions>
    </Dialog>
  );
};
